import React, { useRef, useState } from 'react';
import { Flex } from '@withjoy/joykit';
import SortableList from '@shared/components/SortableList';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { pxToRem } from '@withjoy/joykit/theme';
import { useThrottledWindowResize } from '@shared/utils/hooks/useThrottledWindowResize';
import { SortableVacationRentalTile } from '../SortableVacationRentalTile';
import type { VacationRentalAreaData } from '@shared/components/VacationRentalTile/VacationRentalTile.types';
import { useDragSorting } from '@shared/hooks/useDragSorting';

interface VacationRentalsListProps
  extends Readonly<{
    rentals: ReadonlyArray<VacationRentalAreaData>;
    onReorder?: (reorderedRentals: ReadonlyArray<VacationRentalAreaData>) => void;
    onView: (rental: VacationRentalAreaData) => void;
    onRemove: (rental: VacationRentalAreaData) => void;
  }> {}

export const VacationRentalsList: React.FC<VacationRentalsListProps> = ({ rentals, onReorder, onView, onRemove }) => {
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { sortedItems, isDragActive, handleDragStart, handleDragCancel, handleDragEnd } = useDragSorting({
    items: rentals,
    onReorder,
  });

  // Measure container width for consistent drag overlay sizing
  const measureWidth = useEventCallback(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  });

  useThrottledWindowResize(measureWidth);

  return (
    <Flex
      ref={containerRef}
      flexDirection="column"
      gap={6}
      marginX={6}
      marginTop={5}
      paddingBottom={pxToRem(100)}
    >
      <SortableList
        items={Array.from(sortedItems)}
        renderItem={(rental, index, isItemDragActive) => {
          return (
            <SortableVacationRentalTile
              key={`vacation-rental-tile-${rental.id}-${index}`}
              rental={rental}
              isDragActive={isDragActive}
              onView={onView}
              onRemove={onRemove}
              containerWidth={containerWidth}
              isItemDragActive={isItemDragActive}
            />
          );
        }}
        onChange={() => {}}
        hasDragHandle={true}
        handleDragStart={handleDragStart}
        handleDragEnd={handleDragEnd}
        handleDragCancel={handleDragCancel}
        adjustOverlayScale={false}
      />
    </Flex>
  );
};
